import { EditableTablePreviewResponseV2 } from "@/apis/editable-preview/types";
import { useCategory } from "@/hooks/use-category";
import { cn } from "@/lib/utils";
import { usePreviewStore } from "@/stores/preview";
import { Tabs, TabsList, TabsTrigger } from "@/styles/ui/tabs";
import { Loader, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { PreviewEmptyView } from "./emtpy-view";
import { PreviewLoadingView, TablePreviewLoadingView } from "./loading";
import { PreviewSideBar } from "./sidebar";
import { DimensionIcon } from "./sidebar/dimension-icon";
import { EditablePreviewTable } from "./table/editable-render-table";
import {
  useEditablePreview,
  useEditableTreeView,
  useResetOverrides,
} from "./table/use-editable-preview";

const TableTabHeader = () => {
  const { targetYear: targetYear, setTargetYear: setTargetYear } =
    usePreviewStore((state) => state);

  return (
    <div>
      <Tabs
        value={targetYear}
        defaultValue="CURRENT"
        onValueChange={(value) =>
          setTargetYear(value as "CURRENT" | "PREVIOUS")
        }
        className="flex w-[200px] h-[36px] p-[3px] items-center bg-[#F5F5F5] rounded-[10px] text-primary"
      >
        <TabsList className="grid w-full grid-cols-2 bg-transparent p-0 h-full">
          <TabsTrigger
            value="CURRENT"
            className="basis-0 grow h-full flex items-center justify-center px-2 py-1 rounded-[8px] text-[14px] font-medium text-primary tracking-[0.203px] data-[state=active]:bg-white data-[state=active]:shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] border-0"
            onClick={() => setTargetYear("CURRENT")}
          >
            당기
          </TabsTrigger>
          <TabsTrigger
            value="PREVIOUS"
            className="basis-0 grow h-full flex items-center justify-center px-2 py-1 rounded-[8px] text-[14px] font-medium text-primary tracking-[0.203px] data-[state=active]:bg-white data-[state=active]:shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] border-0"
            onClick={() => setTargetYear("PREVIOUS")}
          >
            전기
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
};

const TableTitle = ({ tableName }: { tableName: string }) => {
  return <div className="title-3-bold text-primary">{tableName}</div>;
};

export const TableContainer = ({
  editablePreview,
  isFetching,
  isError,
  onResetOverrides,
}: {
  editablePreview: EditableTablePreviewResponseV2;
  isFetching: boolean;
  isError: boolean;
  onResetOverrides?: () => void;
}) => {
  const { category } = useCategory();

  return (
    <div
      className={cn(
        "flex flex-col flex-1 min-h-0 transition-all duration-200 ease-in-out relative",
        "w-full max-w-full",
      )}
    >
      <div className="flex flex-col overflow-auto items-start gap-[24px] px-[32px] py-[24px] h-full transition-all duration-300 bg-[#FFFBF0]">
        <div className="flex flex-row justify-between w-full">
          <TableTitle tableName={category?.preRole.label || ""} />
        </div>
        <div className="flex flex-row items-center gap-[12px]">
          <TableTabHeader />
          {editablePreview.hasOverrides && (
            <>
              <span className="caption-2-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
                수정된 항목이 있습니다
              </span>
              <button
                type="button"
                onClick={onResetOverrides}
                className="flex items-center gap-1 px-2 py-1 rounded-md caption-2-medium text-red-500 hover:bg-red-50 transition-colors"
              >
                <Undo2 className="w-3 h-3" />
                전체 초기화
              </button>
              {isFetching && (
                <div className="flex items-center gap-1 mb-3">
                  <Loader className="size-3 text-neutral-500 animate-spin" />
                  <span className="caption-2-semibold text-neutral-500">
                    변경사항 반영중
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        <div className="min-w-0 max-w-full">
          <EditablePreviewTable
            isError={isError}
            editablePreview={editablePreview}
          />
        </div>
      </div>
    </div>
  );
};

export const TablePreviewComponent = () => {
  const {
    tableTreeView: editableTreeView,
    isLoading: isTreeViewLoading,
    error: treeViewError,
  } = useEditableTreeView({ enabled: true });

  const {
    editablePreview,
    isLoading: isPreviewLoading,
    isFetching: isPreviewFetching,
    error: previewError,
  } = useEditablePreview(true);

  const { mutate: resetOverrides } = useResetOverrides();

  const handleResetOverrides = () => {
    resetOverrides(undefined, {
      onSuccess: () => {
        toast.success("모든 수정 사항이 초기화되었습니다.");
      },
      onError: () => {
        toast.error("초기화에 실패했습니다.");
      },
    });
  };

  if (isTreeViewLoading) {
    return <PreviewLoadingView />;
  }

  if (!editablePreview && !editableTreeView?.items.length) {
    return <PreviewEmptyView />;
  }

  return (
    <div className="flex flex-row h-[calc(100vh-100px)] w-full ">
      <div className="h-full transition-all duration-300 ease-in-out flex-shrink-0 relative w-[500px]">
        {editableTreeView && (
          <PreviewSideBar
            tableTreeView={editableTreeView}
            isLoading={isTreeViewLoading}
            error={treeViewError}
          />
        )}
      </div>
      <div className="flex flex-col flex-grow min-w-0 min-h-0 ">
        {isPreviewLoading && <TablePreviewLoadingView />}
        {!isPreviewLoading && editablePreview && (
          <TableContainer
            editablePreview={editablePreview}
            isFetching={isPreviewFetching}
            isError={!!previewError}
            onResetOverrides={handleResetOverrides}
          />
        )}
      </div>
    </div>
  );
};

// 다른 화면이 쓰는 공용 조각은 index 로만 노출한다 (boundaries 규칙 shared-unit-deep-import).
export { DimensionIcon };
